﻿import React, { ChangeEvent, useCallback, useMemo, useRef, useState, FC, forwardRef } from 'react';
import Fuse from 'fuse.js';
import { Callout, DirectionalHint } from '@fluentui/react';
import Style from './appAutoCompleteInput.module.scss';
import { AppTextInput } from 'uikit/inputs';
import { mergeRefs } from '../../../../helpers/mergeRefs';
import IFuseOptions = Fuse.IFuseOptions;

export type AppAutoCompleteInputProps = {
  suggestions: string[];
  value: string;
  onChange: (newValue: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  onFocus?: () => void;
  inputFieldStyle?: string;
  calloutMaxHeight?: number;
  fuseOptions?: IFuseOptions<string>;
};

export const AppAutoCompleteInput = forwardRef<HTMLInputElement, AppAutoCompleteInputProps>((props, ref) => {
  const internalInputRef = useRef<HTMLInputElement | null>(null);
  const { onChange } = props;

  const [showSuggestions, setShowSuggestions] = useState(false);

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      onChange(newValue);
      setShowSuggestions(true);
    },
    [onChange],
  );

  const onFocus = useCallback(() => {
    setShowSuggestions(true);
    props.onFocus?.();
  }, [props]);

  const onSelectSuggestion = useCallback(
    (value: string) => {
      setShowSuggestions(false);
      onChange(value);
    },
    [onChange],
  );

  const fuseInstance = useMemo(() => new Fuse(props.suggestions, props.fuseOptions), [props.suggestions]);

  const filteredSuggestions = useMemo(() => {
    return props.value
      ? fuseInstance
          .search(props.value)
          .map((x) => x.item)
          .slice(0, 5)
      : props.suggestions;
  }, [props.value, fuseInstance, props.suggestions]);

  return (
    <div className={Style.autocompleteInputWrapper}>
      <AppTextInput
        ref={mergeRefs([internalInputRef, ref])}
        onChange={onInputChange}
        value={props.value}
        disabled={props.disabled}
        placeholder={props.placeholder}
        onFocus={onFocus}
        onBlur={props.onBlur}
        invalid={props.invalid}
      />
      {showSuggestions && (
        <Callout
          target={internalInputRef}
          isBeakVisible={false}
          className={Style.callout}
          onRestoreFocus={() => {}}
          onDismiss={() => setShowSuggestions(false)}
          calloutMaxHeight={props.calloutMaxHeight ?? window.innerHeight * 0.6}
          directionalHint={DirectionalHint.bottomLeftEdge}
          styles={() => ({ calloutMain: { width: internalInputRef.current?.clientWidth } })}
        >
          <div className={Style.suggestions}>
            {filteredSuggestions.map((suggestion) => (
              <button key={suggestion} className={Style.suggestionItem} onClick={() => onSelectSuggestion(suggestion)}>
                <span className={'truncate'}>{suggestion}</span>
              </button>
            ))}
          </div>
        </Callout>
      )}
    </div>
  );
});
